/**
 * Cache Invalidation System
 * 
 * Implements aggressive cache invalidation strategies to prevent stale data.
 * Handles version-based invalidation, time-based expiration, and event-driven updates.
 */

import CacheManager from './CacheManager';
import BackgroundSyncService from '../services/BackgroundSyncService';

export interface InvalidationStrategy {
  type: 'immediate' | 'delayed' | 'conditional';
  delay?: number;
  condition?: () => boolean;
}

export class CacheInvalidation {
  private static invalidationListeners: Map<string, Set<() => void>> = new Map();
  private static pendingInvalidations: Map<string, NodeJS.Timeout> = new Map();

  /**
   * Invalidate chunk cache with immediate effect
   */
  static invalidateChunk(chunkId: string, strategy: InvalidationStrategy = { type: 'immediate' }): void {
    if (strategy.type === 'immediate') {
      CacheManager.deleteChunk(chunkId);
      console.log(`🗑️ Invalidated chunk cache: ${chunkId}`);
      this.notifyListeners(`chunk:${chunkId}`);
    } else if (strategy.type === 'delayed' && strategy.delay) {
      const timeoutId = setTimeout(() => {
        CacheManager.deleteChunk(chunkId);
        console.log(`🗑️ Delayed invalidation of chunk cache: ${chunkId}`);
        this.notifyListeners(`chunk:${chunkId}`);
        this.pendingInvalidations.delete(`chunk:${chunkId}`);
      }, strategy.delay);
      this.pendingInvalidations.set(`chunk:${chunkId}`, timeoutId);
    }
  }

  /**
   * Invalidate player data with immediate effect
   */
  static invalidatePlayerData(characterId: string, strategy: InvalidationStrategy = { type: 'immediate' }): void {
    if (strategy.type === 'immediate') {
      CacheManager.invalidatePlayerData(characterId);
      console.log(`🗑️ Invalidated player data cache: ${characterId}`);
      this.notifyListeners(`player:${characterId}`);
      
      // Force background sync to fetch fresh data
      BackgroundSyncService.pause();
      BackgroundSyncService.start();
    } else if (strategy.type === 'delayed' && strategy.delay) {
      const timeoutId = setTimeout(() => {
        CacheManager.invalidatePlayerData(characterId);
        console.log(`🗑️ Delayed invalidation of player data: ${characterId}`);
        this.notifyListeners(`player:${characterId}`);
        this.pendingInvalidations.delete(`player:${characterId}`);
      }, strategy.delay);
      this.pendingInvalidations.set(`player:${characterId}`, timeoutId);
    }
  }

  /**
   * Invalidate market data
   */
  static invalidateMarketData(marketId: string = 'default', strategy: InvalidationStrategy = { type: 'immediate' }): void {
    if (strategy.type === 'immediate') {
      CacheManager.deleteMarketData(marketId);
      console.log(`🗑️ Invalidated market data cache: ${marketId}`);
      this.notifyListeners(`market:${marketId}`);
    } else if (strategy.type === 'delayed' && strategy.delay) {
      const timeoutId = setTimeout(() => {
        CacheManager.deleteMarketData(marketId);
        console.log(`🗑️ Delayed invalidation of market data: ${marketId}`);
        this.notifyListeners(`market:${marketId}`);
        this.pendingInvalidations.delete(`market:${marketId}`);
      }, strategy.delay);
      this.pendingInvalidations.set(`market:${marketId}`, timeoutId);
    }
  }

  /**
   * Invalidate all caches
   */
  static invalidateAll(strategy: InvalidationStrategy = { type: 'immediate' }): void {
    if (strategy.type === 'immediate') {
      CacheManager.clearAll();
      console.log('🗑️ Invalidated all caches');
      this.notifyListeners('all');
    } else if (strategy.type === 'delayed' && strategy.delay) {
      const timeoutId = setTimeout(() => {
        CacheManager.clearAll();
        console.log('🗑️ Delayed invalidation of all caches');
        this.notifyListeners('all');
        this.pendingInvalidations.delete('all');
      }, strategy.delay);
      this.pendingInvalidations.set('all', timeoutId);
    }
  }

  /**
   * Cancel pending invalidation
   */
  static cancelInvalidation(key: string): void {
    const timeoutId = this.pendingInvalidations.get(key);
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.pendingInvalidations.delete(key);
      console.log(`⏸️ Cancelled pending invalidation: ${key}`);
    }
  }

  /**
   * Subscribe to invalidation events
   */
  static onInvalidate(key: string, callback: () => void): () => void {
    if (!this.invalidationListeners.has(key)) {
      this.invalidationListeners.set(key, new Set());
    }
    this.invalidationListeners.get(key)!.add(callback);

    // Return unsubscribe function
    return () => {
      const listeners = this.invalidationListeners.get(key);
      if (listeners) {
        listeners.delete(callback);
      }
    };
  }

  /**
   * Notify all listeners of invalidation
   */
  private static notifyListeners(key: string): void {
    const listeners = this.invalidationListeners.get(key);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback();
        } catch (error) {
          console.error(`Error in invalidation listener for ${key}:`, error);
        }
      });
    }

    // Also notify 'all' listeners
    if (key !== 'all') {
      const allListeners = this.invalidationListeners.get('all');
      if (allListeners) {
        allListeners.forEach(callback => {
          try {
            callback();
          } catch (error) {
            console.error('Error in global invalidation listener:', error);
          }
        });
      }
    }
  }

  /**
   * Set up automatic invalidation based on time
   */
  static setAutoInvalidation(
    key: string,
    interval: number,
    strategy: InvalidationStrategy = { type: 'immediate' }
  ): () => void {
    const intervalId = setInterval(() => {
      console.log(`⏰ Auto-invalidating ${key} after ${interval}ms`);
      
      if (key.startsWith('chunk:')) {
        this.invalidateChunk(key.replace('chunk:', ''), strategy);
      } else if (key.startsWith('player:')) {
        this.invalidatePlayerData(key.replace('player:', ''), strategy);
      } else if (key.startsWith('market:')) {
        this.invalidateMarketData(key.replace('market:', ''), strategy);
      } else if (key === 'all') {
        this.invalidateAll(strategy);
      }
    }, interval);

    // Return cleanup function
    return () => {
      clearInterval(intervalId);
      console.log(`🛑 Stopped auto-invalidation for ${key}`);
    };
  }

  /**
   * Get pending invalidations
   */
  static getPendingInvalidations(): string[] {
    return Array.from(this.pendingInvalidations.keys());
  }

  /**
   * Clear all pending invalidations
   */
  static clearPendingInvalidations(): void {
    this.pendingInvalidations.forEach(timeoutId => clearTimeout(timeoutId));
    this.pendingInvalidations.clear();
    console.log('🧹 Cleared all pending invalidations');
  }
}

export default CacheInvalidation;
